import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Shield, FileText, Clock, CheckCircle, XCircle, Search } from 'lucide-react';

interface Provider {
  id: string;
  name: string;
  registration_number: string;
  fca_number: string;
  primary_contact_name: string;
  primary_email: string;
  phone_number: string;
  verification_status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  last_status_change: string;
  rejection_reason?: string;
  documents: any[];
  verification_history: any[];
  admin_notes: any[];
}

export function ProviderVerification() {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchProviders();
  }, [filter]);

  async function fetchProviders() {
    try {
      setLoading(true);
      setError(null);

      const { data: providers, error } = await supabase
        .from('insurers')
        .select('*, documents:insurer_documents(*), verification_history:insurer_verifications(*), admin_notes:insurer_notes(*)')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setProviders(providers || []);
    } catch (err) {
      console.error('Error fetching providers:', err);
      setError('Failed to load providers');
    } finally {
      setLoading(false);
    }
  }

  async function handleStatusChange(providerId: string, status: 'approved' | 'rejected') {
    try {
      const { error } = await supabase.rpc(
        'update_insurer_verification',
        {
          p_insurer_id: providerId,
          p_status: status,
          p_reason: status === 'rejected' ? rejectionReason : null,
          p_notes: adminNote || null
        }
      );

      if (error) throw error;

      // Clear form and refresh data
      setRejectionReason('');
      setAdminNote('');
      setSelectedProvider(null);
      await fetchProviders();

    } catch (err) {
      console.error('Error updating provider status:', err);
      setError('Failed to update provider status');
    }
  }

  async function addAdminNote(providerId: string) {
    if (!adminNote.trim()) return;

    try {
      const { error } = await supabase.rpc(
        'add_insurer_note',
        {
          p_insurer_id: providerId,
          p_note: adminNote
        }
      );

      if (error) throw error;

      // Clear note and refresh data
      setAdminNote('');
      await fetchProviders();

    } catch (err) {
      console.error('Error adding note:', err);
      setError('Failed to add note');
    }
  }

  const filteredProviders = providers.filter(provider => {
    const matchesFilter = filter === 'all' || provider.verification_status === filter;
    const matchesSearch = searchTerm === '' || 
      provider.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      provider.registration_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      provider.fca_number.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center">
          <Shield className="w-6 h-6 text-indigo-600 mr-2" />
          Provider Verification
        </h2>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search providers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
            className="border rounded-md px-3 py-2"
          >
            <option value="all">All Providers</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Provider List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {filteredProviders.map((provider) => (
            <li key={provider.id}>
              <div className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">{provider.name}</h3>
                        <div className="mt-1 flex items-center">
                          <span className="text-sm text-gray-500">Registration: {provider.registration_number}</span>
                          <span className="mx-2 text-gray-300">|</span>
                          <span className="text-sm text-gray-500">FCA: {provider.fca_number}</span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          provider.verification_status === 'approved' ? 'bg-green-100 text-green-800' :
                          provider.verification_status === 'rejected' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {provider.verification_status === 'approved' ? (
                            <CheckCircle className="w-4 h-4 mr-1" />
                          ) : provider.verification_status === 'rejected' ? (
                            <XCircle className="w-4 h-4 mr-1" />
                          ) : (
                            <Clock className="w-4 h-4 mr-1" />
                          )}
                          {provider.verification_status.charAt(0).toUpperCase() + provider.verification_status.slice(1)}
                        </span>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-col sm:flex-row sm:flex-wrap sm:space-x-6">
                      <div className="mt-2 flex items-center text-sm text-gray-500">
                        <FileText className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" />
                        {provider.documents?.length || 0} documents
                      </div>
                      <div className="mt-2 flex items-center text-sm text-gray-500">
                        <Clock className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" />
                        Applied {new Date(provider.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    <button
                      onClick={() => setSelectedProvider(provider)}
                      className="font-medium text-indigo-600 hover:text-indigo-500"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Provider Details Modal */}
      {selectedProvider && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Provider Details</h3>
                <button
                  onClick={() => setSelectedProvider(null)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <span className="sr-only">Close</span>
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Company Information */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-4">Company Information</h4>
                <dl className="space-y-2">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Company Name</dt>
                    <dd className="mt-1 text-sm text-gray-900">{selectedProvider.name}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Registration Number</dt>
                    <dd className="mt-1 text-sm text-gray-900">{selectedProvider.registration_number}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">FCA Number</dt>
                    <dd className="mt-1 text-sm text-gray-900">{selectedProvider.fca_number}</dd>
                  </div>
                </dl>
              </div>

              {/* Contact Information */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-4">Contact Information</h4>
                <dl className="space-y-2">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Primary Contact</dt>
                    <dd className="mt-1 text-sm text-gray-900">{selectedProvider.primary_contact_name}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Email</dt>
                    <dd className="mt-1 text-sm text-gray-900">{selectedProvider.primary_email}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Phone</dt>
                    <dd className="mt-1 text-sm text-gray-900">{selectedProvider.phone_number}</dd>
                  </div>
                </dl>
              </div>

              {/* Documents */}
              <div className="col-span-full">
                <h4 className="text-sm font-medium text-gray-900 mb-4">Documents</h4>
                <div className="bg-gray-50 rounded-md p-4">
                  {selectedProvider.documents?.length > 0 ? (
                    <ul className="divide-y divide-gray-200">
                      {selectedProvider.documents.map((doc: any) => (
                        <li key={doc.id} className="py-3 flex justify-between items-center">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{doc.document_name}</p>
                            <p className="text-sm text-gray-500">{doc.document_type}</p>
                          </div>
                          <div className="flex items-center space-x-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              doc.verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {doc.verified ? 'Verified' : 'Pending'}
                            </span>
                            <a
                              href={doc.document_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-indigo-600 hover:text-indigo-500 text-sm"
                            >
                              View
                            </a>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500">No documents uploaded</p>
                  )}
                </div>
              </div>

              {/* Verification History */}
              <div className="col-span-full">
                <h4 className="text-sm font-medium text-gray-900 mb-4">Verification History</h4>
                <div className="bg-gray-50 rounded-md p-4">
                  {selectedProvider.verification_history?.length > 0 ? (
                    <ul className="divide-y divide-gray-200">
                      {selectedProvider.verification_history.map((history: any) => (
                        <li key={history.id} className="py-3">
                          <div className="flex items-center justify-between">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              history.status === 'approved' ? 'bg-green-100 text-green-800' :
                              history.status === 'rejected' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {history.status.charAt(0).toUpperCase() + history.status.slice(1)}
                            </span>
                            <span className="text-sm text-gray-500">
                              {new Date(history.changed_at).toLocaleString()}
                            </span>
                          </div>
                          {history.reason && (
                            <p className="mt-1 text-sm text-gray-600">{history.reason}</p>
                          )}
                          {history.notes && (
                            <p className="mt-1 text-sm text-gray-500">{history.notes}</p>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500">No verification history</p>
                  )}
                </div>
              </div>

              {/* Admin Notes */}
              <div className="col-span-full">
                <h4 className="text-sm font-medium text-gray-900 mb-4">Admin Notes</h4>
                <div className="space-y-4">
                  <div>
                    <textarea
                      value={adminNote}
                      onChange={(e) => setAdminNote(e.target.value)}
                      placeholder="Add a note..."
                      rows={3}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                    <div className="mt-2">
                      <button
                        onClick={() => addAdminNote(selectedProvider.id)}
                        disabled={!adminNote.trim()}
                        className="px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Add Note
                      </button>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-md p-4">
                    {selectedProvider.admin_notes?.length > 0 ? (
                      <ul className="divide-y divide-gray-200">
                        {selectedProvider.admin_notes.map((note: any) => (
                          <li key={note.id} className="py-3">
                            <p className="text-sm text-gray-900">{note.note}</p>
                            <div className="mt-1 flex items-center text-xs text-gray-500">
                              <span>{note.admin?.email}</span>
                              <span className="mx-1">•</span>
                              <span>{new Date(note.created_at).toLocaleString()}</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-500">No admin notes</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Status Actions */}
              {selectedProvider.verification_status === 'pending' && (
                <div className="col-span-full border-t pt-4 mt-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 mr-4">
                      <textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        placeholder="Reason for rejection (required for rejecting)"
                        rows={2}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      />
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => handleStatusChange(selectedProvider.id, 'rejected')}
                        disabled={!rejectionReason.trim()}
                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                      >
                        Reject
                      </button>
                      <button
                        onClick={() => handleStatusChange(selectedProvider.id, 'approved')}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                      >
                        Approve
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}